import { useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Menu,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { Build, Close, Delete, Download } from "@mui/icons-material";
import type { Core } from "@rogoag/shared-ts";
import { SAMPLE_TYPE_LABELS, formatTimestamp } from "./PostSamplingIssues.vm";

export interface CoreDetailsDialogProps {
	open: boolean;
	onClose: () => void;
	cores: Core[];
	onFudge: (coreId: string) => void;
	onDelete: (coreId: string) => void;
	onDeleteSelected: (coreIds: string[]) => Promise<void>;
	fudgingCoreId: string | null;
	onDownload: () => void;
}

export function CoreDetailsDialog({
	open,
	onClose,
	cores,
	onFudge,
	onDelete,
	onDeleteSelected,
	fudgingCoreId,
	onDownload,
}: CoreDetailsDialogProps) {
	const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; coreId: string } | null>(null);
	const [selectedCores, setSelectedCores] = useState<Set<string>>(new Set());

	const allSelected = cores.length > 0 && selectedCores.size === cores.length;
	const someSelected = selectedCores.size > 0 && selectedCores.size < cores.length;

	const toggleAll = () => {
		if (allSelected) {
			setSelectedCores(new Set());
		} else {
			setSelectedCores(new Set(cores.map((c) => c.name)));
		}
	};

	const toggleCore = (name: string) => {
		setSelectedCores((prev) => {
			const next = new Set(prev);
			if (next.has(name)) next.delete(name);
			else next.add(name);
			return next;
		});
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper" disableScrollLock>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					Core Details ({cores.length})
					{selectedCores.size > 0 && (
						<Button
							size="small"
							variant="outlined"
							color="error"
							startIcon={<Delete />}
							disabled={fudgingCoreId === "bulk"}
							onClick={async () => {
								await onDeleteSelected(Array.from(selectedCores));
								setSelectedCores(new Set());
							}}
						>
							{fudgingCoreId === "bulk" ? <CircularProgress size={16} /> : `Delete Selected (${selectedCores.size})`}
						</Button>
					)}
				</Box>
				<Box sx={{ display: "flex", gap: 0.5 }}>
					<IconButton onClick={onDownload} size="small" disabled={cores.length === 0}>
						<Download />
					</IconButton>
					<IconButton onClick={onClose} size="small">
						<Close />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent dividers>
				{cores.length === 0 ? (
					<Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
						No cores found.
					</Typography>
				) : (
					<TableContainer>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell padding="checkbox">
										<Checkbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} size="small" />
									</TableCell>
									<TableCell padding="checkbox" />
									<TableCell>ID</TableCell>
									<TableCell>Sample ID</TableCell>
									<TableCell>Original Sample ID</TableCell>
									<TableCell>Bag ID</TableCell>
									<TableCell>Order</TableCell>
									<TableCell>Type</TableCell>
									<TableCell>Start Depth</TableCell>
									<TableCell>End Depth</TableCell>
									<TableCell>Core Length</TableCell>
									<TableCell>Planned Lat,Lon</TableCell>
									<TableCell>Pulled Lat,Lon</TableCell>
									<TableCell>Sampled At</TableCell>
									<TableCell>Scanned At</TableCell>
									<TableCell>Pulled At</TableCell>
									<TableCell>Box UID</TableCell>
									<TableCell>External Ref ID</TableCell>
									<TableCell>Change Type</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{cores.map((core, i) => (
									<TableRow key={`${core.id}-${i}`} selected={selectedCores.has(core.name)}>
										<TableCell padding="checkbox">
											<Checkbox
												checked={selectedCores.has(core.name)}
												onChange={() => toggleCore(core.name)}
												size="small"
											/>
										</TableCell>
										<TableCell padding="checkbox">
											{fudgingCoreId === core.name ? (
												<CircularProgress size={20} />
											) : (
												<IconButton
													size="small"
													onClick={(e) => setMenuAnchor({ el: e.currentTarget, coreId: core.name })}
												>
													<Build fontSize="small" />
												</IconButton>
											)}
										</TableCell>
										<TableCell>{core.id}</TableCell>
										<TableCell>{core.extendedData.sample_id || "—"}</TableCell>
										<TableCell>{core.extendedData.original_sample_id || "—"}</TableCell>
										<TableCell>{core.extendedData.bag_id || "—"}</TableCell>
										<TableCell>{core.extendedData.order || "—"}</TableCell>
										<TableCell>
											{SAMPLE_TYPE_LABELS[core.extendedData.sample_type as number] ??
												core.extendedData.sample_type ??
												"—"}
										</TableCell>
										<TableCell>{core.extendedData.start_depth || "—"}</TableCell>
										<TableCell>{core.extendedData.end_depth || "—"}</TableCell>
										<TableCell>{core.extendedData.core_length_cm || "—"}</TableCell>
										<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
											{core.plannedLatitude ? core.plannedLatitude.toFixed(6) : "0"},
											{core.plannedLongitude ? core.plannedLongitude.toFixed(6) : "0"}
										</TableCell>
										<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
											{core.pulledLatitude ? core.pulledLatitude.toFixed(6) : "0"},
											{core.pulledLongitude ? core.pulledLongitude.toFixed(6) : "0"}
										</TableCell>
										<TableCell>{formatTimestamp(core.extendedData.sampled_at)}</TableCell>
										<TableCell>{formatTimestamp(core.extendedData.scanned_at)}</TableCell>
										<TableCell>{formatTimestamp(core.extendedData.pulled_at)}</TableCell>
										<TableCell>{core.extendedData.box_uid || "—"}</TableCell>
										<TableCell>{core.extendedData.external_reference_id || "—"}</TableCell>
										<TableCell>{core.extendedData.change_type || "—"}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</DialogContent>
			<Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
				<MenuItem
					onClick={() => {
						if (menuAnchor) {
							onFudge(menuAnchor.coreId);
							setMenuAnchor(null);
						}
					}}
				>
					Fudge
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (menuAnchor) {
							onDelete(menuAnchor.coreId);
							setMenuAnchor(null);
						}
					}}
					sx={{ color: "error.main" }}
				>
					Delete
				</MenuItem>
			</Menu>
		</Dialog>
	);
}
